"use client";

import { useState } from "react";
import Image, { StaticImageData } from "next/image";
import checkIcon from "@/assets/images/prebuilt-ai-tab/tick.svg";
import CommonButton from "@/components/common/CommonButton";

export interface FeatureTabItem {
  intro: string;
  title: string;
  description?: string;
  buttonText?: string;
  linkUrl?: string;
  featureImg: StaticImageData;
  featuredVideo?: string;
  featureList: { text: string }[];
}

interface FeaturedTabsProps {
  featureListTabsData: FeatureTabItem[];
}

const FeaturedTabs: React.FC<FeaturedTabsProps> = ({ featureListTabsData }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeFeature = featureListTabsData[activeIndex];

  return (
    <div>
      <div className="mx-auto mb-8 flex flex-wrap justify-center gap-2 lg:mb-10">
        {featureListTabsData.map((item, index) => {
          const isActive = index === activeIndex;
          return (
            <button
              key={index}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-normal transition-colors lg:px-4 lg:py-2 ${isActive
                ? "border-[#202020] bg-white shadow-sm"
                : "border-[#E0E0EA] bg-white text-[#4A4A4A] hover:border-[#C5C5D8]"
                }`}
            >
              <span
                className={
                  isActive
                    ? "bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent"
                    : ""
                }
              >
                {item.intro}
              </span>
            </button>
          );
        })}
      </div>
      <div className="relative overflow-hidden rounded-3xl p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col items-stretch gap-6 xl:flex-row xl:items-center xl:gap-8 xl:justify-between">
          <div className="w-full xl:w-1/2 xl:max-w-[520px]">
            <div className="inline-flex rounded-full bg-[#F7F5FF] px-3 py-1 text-sm font-semibold shadow-sm">
              <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                {activeFeature.intro}
              </span>
            </div>
            <h3 className="mt-4 text-[20px] font-semibold leading-[36px] text-[#202020] lg:text-[22px]">
              {activeFeature.title}
            </h3>
            {activeFeature.description && (
              <p className="mt-2 text-[14px] leading-relaxed text-[#202020] lg:text-[16px]">
                {activeFeature.description}
              </p>
            )}
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-[14px] text-[#202020] sm:gap-x-8 lg:text-[15px]">
              {activeFeature.featureList.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-[15px]"
                >
                  <Image
                    src={checkIcon}
                    alt="Feature included"
                    width={18}
                    height={18}
                    className="flex-shrink-0"
                  />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
            {activeFeature.buttonText && activeFeature.linkUrl && (
              <div className="mt-6">
                <CommonButton
                  text={activeFeature.buttonText}
                  url={activeFeature.linkUrl}
                  isSecondaryBg
                  textWeightMedium
                />
              </div>
            )}
          </div>
          <div className="relative xl:flex hidden w-full overflow-hidden rounded-2xl h-[500px] xl:flex-1">
            {activeFeature.featuredVideo ? (
              <video
                key={activeFeature.featuredVideo}
                className="h-full w-full rounded-2xl object-cover"
                autoPlay
                loop
                muted
                playsInline
                controls={false}
              >
                <source src={activeFeature.featuredVideo} type="video/mp4" />
              </video>
            ) : (
              <Image
                src={activeFeature.featureImg}
                alt={activeFeature.intro}
                className="h-full w-full rounded-2xl object-cover"
                priority
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedTabs;
